'use client';

import { Clock, Lock, Search } from 'lucide-react';
import type { AgentId, AgentRestriction, AgentRestrictions, AgentType } from '@/lib/agent-types';

export type { AgentRestriction, AgentType } from '@/lib/agent-types';

interface AgentSelectorProps {
  selectedAgent: AgentType;
  onAgentChange: (agent: AgentType) => void;
  restrictions?: AgentRestrictions;
  onRestrictedAgentClick?: (agent: AgentId, restriction: AgentRestriction) => void;
}

export default function AgentSelector({ selectedAgent, onAgentChange, restrictions = {}, onRestrictedAgentClick }: AgentSelectorProps) {
  const agents = [
    {
      id: 'deepresearch' as const,
      name: 'Deepresearch',
      icon: Search,
    },
  ];

  return (
    <div className="flex items-center gap-2">
      {agents.map((agent) => {
        const Icon = agent.icon;
        const isSelected = selectedAgent === agent.id;
        const restriction = restrictions[agent.id];
        const isRestricted = !!restriction;
        const RestrictionIcon = restriction === 'daily-limit' ? Clock : Lock;
        const restrictionTitle = restriction === 'daily-limit' ? 'Daily limit reached' : 'Challenge/VIP only';

        return (
          <button
            key={agent.id}
            type="button"
            aria-label={agent.name}
            onClick={() => {
              if (restriction) {
                onRestrictedAgentClick?.(agent.id, restriction);
                return;
              }
              onAgentChange(isSelected ? null : agent.id);
            }}
            aria-disabled={isRestricted}
            title={isRestricted ? restrictionTitle : agent.name}
            className={`relative flex h-8 w-8 items-center justify-center gap-0 rounded-full p-0 text-xs font-medium transition-all cursor-pointer sm:h-auto sm:w-auto sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-sm ${
              isRestricted
                ? restriction === 'daily-limit'
                  ? 'border border-[#f98715]/20 bg-[#f98715]/8 text-white/60'
                  : 'border border-white/8 bg-white/[0.03] text-white/45'
                : isSelected
                  ? 'bg-[#ffffff]/8 border text-white border-[#ffffff]/8'
                  : ' text-white border border-[#ffffff]/8'
            }`}>
            <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">{agent.name}</span>
            {isRestricted && (
              <RestrictionIcon
                className="hidden h-3.5 w-3.5 sm:block"
                data-testid={`agent-${agent.id}-${restriction}-icon`}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
